// src/components/dashboard/settings/ProviderManagement.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  TablePagination,
} from "@mui/material";

// Core Guaranteed MUI Icons
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

const ProviderManagement = () => {
  // Live Dark/Light Mode Synchronizer
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Practice Filter State
  const [selectedPractice, setSelectedPractice] = useState("");

  // Exact dataset from your schema with realistic clinic extensions
  const [rows, setRows] = useState([
    {
      id: 199,
      firstName: "usman22",
      lastName: "Parrot",
      createdBy: "Awais09",
      type: "Skilled",
      deaNumber: "111",
      practice: "Main Clinic",
    },
    {
      id: 274,
      firstName: "usman22",
      lastName: "parrot2",
      createdBy: "Awais09",
      type: "Skilled",
      deaNumber: "",
      practice: "Outpatient Center",
    },
    {
      id: 311,
      firstName: "abc",
      lastName: "testing",
      createdBy: "truevv09",
      type: "Skilled",
      deaNumber: "",
      practice: "Main Clinic",
    },
    {
      id: 312,
      firstName: "Dean",
      lastName: "test",
      createdBy: "truevv",
      type: "Skilled",
      deaNumber: "",
      practice: "Specialty Neuro Rehab",
    },
    {
      id: 315,
      firstName: "Dr. Sarah",
      lastName: "Jenkins",
      createdBy: "Awais09",
      type: "Physician",
      deaNumber: "BJ8891024",
      practice: "Main Clinic",
    },
    {
      id: 320,
      firstName: "Elena",
      lastName: "Rostova",
      createdBy: "Dr. Usman",
      type: "Skilled",
      deaNumber: "RN5501923",
      practice: "Telehealth Care",
    },
  ]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [openModal, setOpenModal] = useState(false);

  // New Provider Modal State
  const [newEntry, setNewEntry] = useState({
    firstName: "",
    lastName: "",
    type: "Skilled",
    deaNumber: "",
    createdBy: "Awais09",
    practice: "Main Clinic",
  });

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const tableHeaderBg = isDark ? "#162238" : "#F4F9F9";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.04)";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleDelete = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleCreate = () => {
    if (!newEntry.firstName.trim() || !newEntry.lastName.trim()) return;
    const nextId = Math.max(...rows.map((r) => r.id), 0) + 1;
    setRows([{ id: nextId, ...newEntry }, ...rows]);
    setNewEntry({
      firstName: "",
      lastName: "",
      type: "Skilled",
      deaNumber: "",
      createdBy: "Awais09",
      practice: "Main Clinic",
    });
    setOpenModal(false);
  };

  const getTypeBadge = (type) => {
    if (type === "Physician" || type.includes("Doctor")) {
      return { bg: pastelTealBg, color: tealButton };
    }
    if (type === "Skilled" || type.includes("Nurse")) {
      return { bg: isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7", color: isDark ? "#34D399" : "#15803D" };
    }
    return { bg: isDark ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9", color: secondaryTextColor };
  };

  // Filter Logic
  const filteredRows = rows.filter((row) => {
    if (!selectedPractice) return true;
    return row.practice === selectedPractice;
  });

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryTextColor,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg,
          },
          "&.Mui-selected": {
            backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg,
            color: tealButton,
            fontWeight: 700,
          },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP HEADER & TITLE BAR */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <SecurityOutlinedIcon sx={{ fontSize: "1.7rem" }} />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.35rem" },
                  fontWeight: 800,
                  color: primaryTextColor,
                  letterSpacing: "-0.3px",
                  lineHeight: 1.2,
                }}
              >
                Provider Management
              </Typography>
              <Chip
                label="Licensed Providers"
                size="small"
                sx={{
                  height: "22px",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  backgroundColor: pastelTealBg,
                  color: tealButton,
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: secondaryTextColor,
                fontWeight: 500,
                mt: 0.3,
              }}
            >
              Manage healthcare providers, clinical specialties, and DEA certifications
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* 2. PRACTICE FILTER + ADD PROVIDER TOOLBAR */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "18px 24px" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontSize: "0.84rem",
              fontWeight: 700,
              color: labelTeal,
              minWidth: "65px",
            }}
          >
            Practice:
          </Typography>
          <Select
            displayEmpty
            size="small"
            value={selectedPractice}
            onChange={(e) => setSelectedPractice(e.target.value)}
            IconComponent={KeyboardArrowDownRoundedIcon}
            MenuProps={menuProps}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
                    All Practices
                  </Typography>
                );
              }
              return selected;
            }}
            sx={{
              width: { xs: "100%", sm: "280px" },
              height: "40px",
              borderRadius: "6px",
              backgroundColor: controlBg,
              fontSize: "0.85rem",
              color: primaryTextColor,
              "& fieldset": { borderColor: controlBorder },
              "&:hover fieldset": { borderColor: tealMain },
              "&.Mui-focused fieldset": { borderColor: tealMain },
              "& .MuiSelect-icon": { color: secondaryTextColor },
            }}
          >
            <MenuItem value="">All Practices</MenuItem>
            <MenuItem value="Main Clinic">Main Clinic</MenuItem>
            <MenuItem value="Outpatient Center">Outpatient Center</MenuItem>
            <MenuItem value="Specialty Neuro Rehab">Specialty Neuro Rehab</MenuItem>
            <MenuItem value="Telehealth Care">Telehealth Care</MenuItem>
          </Select>
        </Box>

        <Button
          variant="contained"
          onClick={() => setOpenModal(true)}
          startIcon={<AddRoundedIcon sx={{ fontSize: "1.15rem !important" }} />}
          sx={{
            height: "40px",
            backgroundColor: addGreen,
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.82rem",
            borderRadius: "6px",
            px: 2.5,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#046337",
              boxShadow: "none",
            },
          }}
        >
          Add Provider
        </Button>
      </Box>

      {/* 3. PROVIDER 7-COLUMN TABLE */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "16px",
          border: `1px solid ${softCardBorder}`,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "620px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 920 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: tealButton,
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    backgroundColor: tableHeaderBg,
                    borderBottom: `1px solid ${softCardBorder}`,
                    py: 1.5,
                  },
                }}
              >
                <TableCell sx={{ width: "90px" }}>ID</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>DEA number</TableCell>
                <TableCell align="right" sx={{ width: "90px", pr: 3 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 8, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.88rem", fontWeight: 600 }}>
                      No providers registered
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const typeBadge = getTypeBadge(row.type);

                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          transition: "background-color 0.15s ease",
                          "&:hover": { backgroundColor: rowHoverBg },
                          "& td": {
                            borderBottom: `1px solid ${
                              isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                            }`,
                            py: 1.4,
                          },
                        }}
                      >
                        {/* ID Badge */}
                        <TableCell>
                          <Box
                            sx={{
                              display: "inline-block",
                              px: 1,
                              py: 0.25,
                              borderRadius: "6px",
                              backgroundColor: pastelTealBg,
                              color: tealButton,
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              fontFamily: "monospace",
                            }}
                          >
                            {row.id}
                          </Box>
                        </TableCell>

                        {/* First Name */}
                        <TableCell sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.86rem" }}>
                          {row.firstName}
                        </TableCell>

                        {/* Last Name */}
                        <TableCell sx={{ color: primaryTextColor, fontSize: "0.86rem", fontWeight: 600 }}>
                          {row.lastName}
                        </TableCell>

                        {/* Created By */}
                        <TableCell sx={{ color: secondaryTextColor, fontSize: "0.82rem", fontWeight: 600 }}>
                          {row.createdBy}
                        </TableCell>

                        {/* Type Badge */}
                        <TableCell>
                          <Chip
                            label={row.type}
                            size="small"
                            sx={{
                              height: "22px",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              borderRadius: "6px",
                              backgroundColor: typeBadge.bg,
                              color: typeBadge.color,
                            }}
                          />
                        </TableCell>

                        {/* DEA Number */}
                        <TableCell sx={{ fontFamily: "monospace", fontWeight: 700, color: tealButton, fontSize: "0.82rem" }}>
                          {row.deaNumber || "—"}
                        </TableCell>

                        {/* Action */}
                        <TableCell align="right" sx={{ pr: 2.5 }}>
                          <Tooltip title="Delete Provider">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(row.id)}
                              sx={{
                                color: "#EF4444",
                                p: 0.6,
                                borderRadius: "6px",
                                "&:hover": {
                                  backgroundColor: "rgba(239, 68, 68, 0.08)",
                                },
                              }}
                            >
                              <DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom Pagination Bar */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${softCardBorder}`,
            color: secondaryTextColor,
            "& .MuiTablePagination-select": {
              color: primaryTextColor,
              fontWeight: 600,
            },
            "& .MuiTablePagination-selectIcon": {
              color: secondaryTextColor,
            },
          }}
        />
      </Box>

      {/* 4. ADD PROVIDER MODAL DIALOG */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cardBg,
            borderRadius: "16px",
            border: `1.5px solid ${cardBorder}`,
            color: primaryTextColor,
            p: 1.5,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.15rem", color: primaryTextColor, pb: 1 }}>
          Add Healthcare Provider
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                First Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Usman"
                value={newEntry.firstName}
                onChange={(e) => setNewEntry({ ...newEntry, firstName: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Last Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Rehman"
                value={newEntry.lastName}
                onChange={(e) => setNewEntry({ ...newEntry, lastName: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Provider Type
              </Typography>
              <Select
                fullWidth
                size="small"
                value={newEntry.type}
                onChange={(e) => setNewEntry({ ...newEntry, type: e.target.value })}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                sx={{
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                  "& .MuiSelect-icon": { color: secondaryTextColor },
                }}
              >
                <MenuItem value="Skilled">Skilled</MenuItem>
                <MenuItem value="Physician">Physician</MenuItem>
                <MenuItem value="Nurse Practitioner">Nurse Practitioner</MenuItem>
                <MenuItem value="Therapist">Therapist</MenuItem>
              </Select>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                DEA Number
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 111 or BJ991"
                value={newEntry.deaNumber}
                onChange={(e) => setNewEntry({ ...newEntry, deaNumber: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
              Practice Assignment
            </Typography>
            <Select
              fullWidth
              size="small"
              value={newEntry.practice}
              onChange={(e) => setNewEntry({ ...newEntry, practice: e.target.value })}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              sx={{
                borderRadius: "6px",
                backgroundColor: controlBg,
                color: primaryTextColor,
                fontSize: "0.85rem",
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
                "& .MuiSelect-icon": { color: secondaryTextColor },
              }}
            >
              <MenuItem value="Main Clinic">Main Clinic</MenuItem>
              <MenuItem value="Outpatient Center">Outpatient Center</MenuItem>
              <MenuItem value="Specialty Neuro Rehab">Specialty Neuro Rehab</MenuItem>
              <MenuItem value="Telehealth Care">Telehealth Care</MenuItem>
            </Select>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setOpenModal(false)}
            sx={{
              color: secondaryTextColor,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{
              backgroundColor: addGreen,
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              px: 2.8,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#046337",
                boxShadow: "none",
              },
            }}
          >
            Save Provider
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderManagement;